// web-app/src/utils/runFrameMigration.js
//
// Read-time heal for persisted cabinet runs (spec §8.6). Rooms written before
// runs moved to the room-wall frame carry (a) manual segments naming STRUCTURE
// walls, (b) auto segments with params measured on the kitchen-CLIPPED wall
// and no frame to say so, (c) appliances no bay stamp ever reached. Each is
// fixed here, on read, from the room alone, and written back by whichever
// room-level write comes next — the same contract as the corner-join
// migration this composes (#1494). Pure; identity when nothing changes.

import { joinPersistedAutoRuns } from './cornerJoinMigration';
import { stampApplianceBays } from '@shared-lib/cabinet-runs/ownership';
import { runAliases } from '@shared-lib/cabinet-runs/runIds';
import {
  buildSegmentWallResolver,
  roomWallForFrame,
} from '@shared-lib/cabinet-runs/segmentWall';
import { rebaseRunFrames } from '@shared-lib/cabinet-runs/wallFrame';
import {
  getWallAxes,
  worldToWallParam,
} from '@shared-lib/kitchen-assembly/wallMath';
import { buildCabinetWallResolver } from '@shared-lib/kitchen-assembly/cabinetLayout/resolveCabinetWall';

/**
 * Room walls backing each structure wall id, via `alignment.structureWallId`.
 * ALL of them: one structure wall routinely backs several room walls of one
 * room, and those siblings are collinear by construction — so the same-line
 * guard in `reconcileFrame` cannot reject a wrong pick, and taking insertion
 * order would silently re-measure a run from the wrong sub-segment's corner.
 * The stored frame decides, in `migrateRun`.
 */
function byStructureWallId(walls) {
  const m = new Map();
  for (const w of walls) {
    const sid = w?.alignment?.structureWallId;
    if (!sid) continue;
    const siblings = m.get(sid);
    if (siblings) siblings.push(w);
    else m.set(sid, [w]);
  }
  return m;
}

/**
 * One run → its canonical form, or the same reference when nothing applies.
 * `onWallId(oldStructureId, newRoomWallId)` is called for each segment whose id
 * moves, so the caller can carry the run's OBJECTS across the same rename.
 * @returns {Object} the run
 */
function migrateRun(run, resolver, structureMap, clippedResolver, onWallId) {
  const segs = run?.segments || [];
  let touched = false;
  // Which frame were the params measured in? Stored frame when present;
  // for a frameless AUTO real segment, the generator's clipped wall.
  const fromWall = (s) => {
    if (s.wallFrame) {
      return {
        alignment: { bottomLeft: s.wallFrame.a, bottomRight: s.wallFrame.b },
      };
    }
    if (run.source === 'auto' && (s.kind || 'real') === 'real') {
      return clippedResolver().resolve({ id: s.wallId, kind: 'real' });
    }
    return null;
  };
  const segments = segs.map((s) => {
    if (s.freestanding || !s.wallId) return s;
    const isRoomWall = resolver.wallById.has(s.wallId);
    if (isRoomWall && s.wallFrame) return s; // canonical already
    if (!isRoomWall) {
      const siblings = structureMap.get(s.wallId);
      if (!siblings || !s.wallFrame) return s; // cannot migrate from the room alone
      // Which sibling? The one on the frame's line whose extent overlaps it
      // most — the same rule the resolver applies when a segment's wall id is
      // gone. None on that line means the frame is not this structure wall's
      // after all: leave it, like every other unprovable case.
      const rw = roomWallForFrame(s.wallFrame, siblings);
      if (!rw) return s;
      touched = true;
      onWallId(s.wallId, rw.id);
      return { ...s, wallId: rw.id };
    }
    // room wall id, no frame: only an auto run knows its measured frame
    if (run.source === 'auto' && (s.kind || 'real') === 'real') touched = true;
    return s;
  });
  if (!touched) return run;
  const { run: rebased, actions } = rebaseRunFrames(
    { ...run, segments },
    fromWall,
    (s) => resolver.resolve(s)
  );
  if (actions.some((a) => a.action === 'orphan' || a.action === 'missing'))
    return run;
  return rebased;
}

/**
 * Which of `candidateIds` an object at `centre` belongs to: the wall whose own
 * span the centre projects INSIDE. One structure wall backs several collinear
 * room walls, so the id alone cannot say which sub-segment an object stands on
 * — its position can. Falls back to the first candidate (a single candidate is
 * the overwhelmingly common case, and a centre slightly off every span is
 * still better placed on one of them than left in the old id space).
 */
function wallForObject(candidateIds, centre, wallById) {
  if (centre) {
    for (const id of candidateIds) {
      const wall = wallById.get(id);
      const axes = wall && getWallAxes(wall);
      if (!axes) continue;
      const p = worldToWallParam(wall, centre);
      if (p != null && p >= 0 && p <= axes.wallLen) return id;
    }
  }
  return candidateIds[0];
}

/**
 * Carry a migrated run's OBJECTS across the same structure→room wall rename its
 * segments took.
 *
 * The engine stamps `parentWallIds: [seg.wall.id]` on everything a run owns, so
 * a run drawn against a structure wall left its cabinets naming that structure
 * wall. `applyBayOverride` reads the wall off the OBJECT (`bayWallId`), so
 * migrating the segments alone made the FIRST override on a legacy manual run a
 * silent no-op: no segment carried the structure id, the resolver missed it,
 * and the edit returned null. (Only the first — any other re-solve rewrites
 * `parentWallIds`.)
 *
 * Pure; returns `objects` by reference when nothing moves.
 *
 * @param {Object[]} objects
 * @param {{ ids: Set<string>, renames: Map<string, string[]> }[]} perRun
 * @param {Map<string, Object>} wallById
 */
function remapObjectParentWalls(objects, perRun, wallById) {
  if (!perRun.length) return objects;
  let changed = false;
  const next = objects.map((o) => {
    const old = o?.parentWallIds?.[0];
    if (!old || !o.runId) return o;
    const entry = perRun.find((e) => e.ids.has(o.runId));
    const candidates = entry?.renames.get(old);
    if (!candidates) return o;
    const id = wallForObject(candidates, o.center, wallById);
    if (!id || id === old) return o;
    changed = true;
    return { ...o, parentWallIds: [id, ...o.parentWallIds.slice(1)] };
  });
  return changed ? next : objects;
}

/**
 * @param {Object} roomInput
 * @returns {{ room: Object, joined: string[][], migrated: string[] }}
 */
export function healPersistedRuns(roomInput) {
  // Most rooms have no runs at all, and the rooms snapshot heals every one of
  // them: hand those straight back. `joinPersistedAutoRuns` would otherwise
  // return a NEW object carrying a `cabinetRuns: []` the room never had — a
  // re-render per snapshot, and an invented field riding the next write.
  if (!(roomInput?.cabinetRuns || []).length) {
    return { room: roomInput, joined: [], migrated: [] };
  }
  const { room: joinedRoom, joined } = joinPersistedAutoRuns(roomInput);
  const runs = joinedRoom?.cabinetRuns || [];
  if (!runs.length) return { room: joinedRoom, joined, migrated: [] };

  const resolver = buildSegmentWallResolver(joinedRoom);
  const structureMap = byStructureWallId(resolver.walls);
  let clipped = null;
  const clippedResolver = () =>
    clipped ||
    (clipped = buildCabinetWallResolver(joinedRoom, joinedRoom?.cabinetLayout));

  const migrated = [];
  // Per migrated run: the ids its objects answer to, and old wall id → the room
  // wall ids its segments moved to. Collected per run and kept only when the
  // run actually migrated — `migrateRun` returns the ORIGINAL run when a rebase
  // orphans, and its objects must not move either.
  const perRun = [];
  const nextRuns = runs.map((r) => {
    const renames = new Map();
    const out = migrateRun(
      r,
      resolver,
      structureMap,
      clippedResolver,
      (from, to) => {
        const to0 = renames.get(from);
        if (to0) {
          if (!to0.includes(to)) to0.push(to);
        } else renames.set(from, [to]);
      }
    );
    if (out === r) return out;
    migrated.push(r.id);
    if (renames.size) {
      perRun.push({ ids: new Set([out.id, ...runAliases(out)]), renames });
    }
    return out;
  });
  const runsChanged = migrated.length > 0;
  // One reference for both the call and the comparison: `stampApplianceBays`
  // returns its input when nothing changes, and two `|| []` literals never
  // compare equal.
  const objects0 = joinedRoom.objects || [];
  // Objects follow their run's rename BEFORE the bay stamp, so the stamp sees
  // the canonical ids.
  const remapped = remapObjectParentWalls(objects0, perRun, resolver.wallById);
  const objects = stampApplianceBays(remapped, runsChanged ? nextRuns : runs);
  const objectsChanged = objects !== objects0;
  if (!runsChanged && !objectsChanged)
    return { room: joinedRoom, joined, migrated };
  return {
    room: {
      ...joinedRoom,
      ...(runsChanged ? { cabinetRuns: nextRuns } : {}),
      ...(objectsChanged ? { objects } : {}),
    },
    joined,
    migrated,
  };
}

/**
 * The save's diff basis, with the runs the read-time healing took out put
 * back, so a write of `cabinetRuns` removes them on the server.
 *
 * The store holds the HEALED room, and a save is operations from the store's
 * copy (`roomSave`): a run the corner join absorbed was never in that copy,
 * so no write ever removed it. The joined run was saved and the absorbed one
 * stayed beside it, holding the same appliance — and the next load, finding
 * one run left to join, kept it as its own run (EC6A20EB: the range run
 * under the joined bottom-and-range run, after the first drag).
 *
 * @param {Object|null} base        the store's copy the save diffs from
 * @param {Array|undefined} serverRuns the room's runs as the server holds them
 * @param {string[]|null} fields    the fields the save writes (null = all)
 * @returns {Object|null}
 */
export function withRunsHealedAway(base, serverRuns, fields) {
  if (!base || !Array.isArray(serverRuns) || !serverRuns.length) return base;
  if (fields && !fields.includes('cabinetRuns')) return base;
  const held = base.cabinetRuns || [];
  const ids = new Set(held.map((r) => r?.id));
  const gone = serverRuns.filter((r) => r?.id && !ids.has(r.id));
  if (!gone.length) return base;
  return { ...base, cabinetRuns: [...held, ...gone] };
}
